/**
 * Shared FAQ data generation for structured data.
 * These functions return plain text only (no React nodes) for use in server components.
 */

import {
  capitalize,
  withIndefiniteArticle
} from '@paction-2.0/util/formatters';

export interface FAQStructuredDataItem {
  question: string;
  answerText: string;
}

/**
 * Distinct screening names from a breed's parent health-testing tables
 * (breed_profiles.parent_health_testing), in table order. Structurally typed
 * so callers can pass the loader's BreedDetail field directly. OFA names the
 * same screening differently per breed ("Cardiac" vs "Cardiac Evaluation"),
 * so prefix-overlapping names merge into the more specific one.
 */
export function extractHealthScreeningNames(
  parentHealthTesting?: Array<{
    breedName?: string | null;
    healthTests?: Array<{ screening?: string | null }> | null;
  }> | null
): string[] {
  const names: string[] = [];
  for (const parent of parentHealthTesting ?? []) {
    for (const test of parent.healthTests ?? []) {
      const name = test.screening?.trim();
      if (!name) continue;
      const lower = name.toLowerCase();
      const overlapIndex = names.findIndex((kept) => {
        const keptLower = kept.toLowerCase();
        return keptLower.startsWith(lower) || lower.startsWith(keptLower);
      });
      if (overlapIndex === -1) {
        names.push(name);
      } else if (name.length > names[overlapIndex]!.length) {
        names[overlapIndex] = name;
      }
    }
  }
  return names;
}

const MAX_FAQ_SCREENINGS = 6;

/**
 * One breed-specific sentence naming the top OFA/CHIC screenings, appended to
 * the "what should I look for in a breeder" FAQ answer so the answer is
 * genuinely differentiated per breed (visible text and FAQPage JSON-LD must
 * carry it identically). Null when the breed has no health-testing data —
 * the template answer then stays unchanged.
 */
export function buildHealthScreeningSentence(
  breedName: string,
  healthScreenings?: string[] | null
): string | null {
  const names = (healthScreenings ?? [])
    .map((name) => name.trim())
    .filter((name) => name.length > 0)
    .slice(0, MAX_FAQ_SCREENINGS);
  if (names.length === 0) return null;
  const list =
    names.length > 1
      ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
      : names[0];
  return `Key health screenings for ${breedName} parents include ${list}.`;
}

/**
 * Generate breed-only FAQ items for structured data (no location).
 * Used on /puppies/[breed] pages to provide general breed buying guidance.
 */
export function generateBreedFAQStructuredData(
  breedName: string,
  nicknames?: string[] | null,
  healthScreenings?: string[] | null
): FAQStructuredDataItem[] {
  const healthSentence = buildHealthScreeningSentence(
    breedName,
    healthScreenings
  );
  const items: FAQStructuredDataItem[] = [];

  if (nicknames && nicknames.length > 0) {
    const nicknameList = nicknames.slice(0, 5).join(', ');
    items.push({
      question: `What is ${withIndefiniteArticle(breedName)} also called?`,
      answerText: `${capitalize(withIndefiniteArticle(breedName))} is also commonly known as ${nicknameList}. These are popular alternative names and search terms used for this breed across Canada.`
    });
  }

  items.push(
    {
      question: `How much do ${breedName} puppies cost in Canada?`,
      answerText: `${breedName} puppy prices in Canada typically vary based on factors like breeder reputation, health testing, lineage and breeding rights. Paction recommends contacting breeders directly through our platform to get current pricing. Always prioritize breeders who provide health guarantees and proper documentation. It's also important to budget for the ongoing costs of owning a dog. For that, Paction has developed The Ultimate Dog Cost Calculator.`
    },
    {
      question: `Where can I find reputable ${breedName} breeders?`,
      answerText: `You can find reputable ${breedName} breeders right here on Paction. Paction features verified breeders across Canada who raise healthy, well-socialized puppies. Use the search results above to view current Listings and Waitlists from breeders. Join a Global Waitlist and Paction will send you a message when we've located an excellent breeder for you.`
    },
    {
      question: `How do I find a responsible ${breedName} breeder?`,
      answerText: `Finding a responsible ${breedName} breeder starts with understanding how the breeder plans their litters and not just whether the dogs are registered. Responsible breeders prioritize genetic and health screening of both parents and transparent lineage information. On Paction, breeders commit to intentional and thoughtful pairings that minimize inherited disease risk and they provide clear information about their breeding decisions. To understand more about responsible breeding programs that prioritize health and genetic screening of breeding pairs, check out Making Science Simple: Canine Genetics for Puppy Shoppers.`
    },
    {
      question: `What should I look for in ${withIndefiniteArticle(breedName)} breeder?`,
      answerText: `When choosing ${withIndefiniteArticle(breedName)} breeder, look for breeders who perform health testing on parent dogs, provide health guarantees, socialize puppies from an early age, and welcome visits to their breeding program.${healthSentence ? ` ${healthSentence}` : ''} Reputable breeders will be transparent about their breeding practices and happy to answer your questions. To better understand why health testing on the parent dogs is important, check out Paction's Making Science Simple: Canine Genetics for Puppy Shoppers. For more educational information, explore Expert Hub.`
    },
    {
      question: `How can I avoid puppy scams when looking for ${withIndefiniteArticle(breedName)}?`,
      answerText: `Puppy scams are unfortunately common when searching for popular breeds like ${breedName}. Red flags include breeders unwilling to show health testing, pressure to send deposits quickly, inconsistent information, or puppies offered without clear planning details. Paction reduces scam risk by working with breeders who verify their identities and commit to transparent, documented breeding practices giving puppy buyers a safer starting point.`
    }
  );

  return items;
}

/**
 * Generate location FAQ items for structured data.
 * Returns only the question and plain text answer (no React nodes).
 */
export function generateLocationFAQStructuredData(
  breedName: string,
  locationName: string,
  provinceName: string,
  isCity: boolean,
  nicknames?: string[] | null,
  healthScreenings?: string[] | null
): FAQStructuredDataItem[] {
  const healthSentence = buildHealthScreeningSentence(
    breedName,
    healthScreenings
  );
  const locationContext = isCity
    ? `${locationName}, ${provinceName}`
    : locationName;

  const items: FAQStructuredDataItem[] = [];

  if (nicknames && nicknames.length > 0) {
    const nicknameList = nicknames.slice(0, 5).join(', ');
    items.push({
      question: `What is ${withIndefiniteArticle(breedName)} also called?`,
      answerText: `${capitalize(withIndefiniteArticle(breedName))} is also commonly known as ${nicknameList}. These are popular alternative names and search terms used for this breed across Canada.`
    });
  }

  items.push(
    {
      question: `How much do ${breedName} puppies cost in ${locationName}?`,
      answerText: `${breedName} puppy prices in ${locationContext} typically vary based on factors like breeder reputation, health testing, lineage and breeding rights. Paction recommends contacting breeders directly through our platform to get current pricing. Always prioritize breeders who provide health guarantees and proper documentation. It's also important to budget for the ongoing costs of owning a dog. For that, Paction has developed The Ultimate Dog Cost Calculator.`
    },
    {
      question: `Where can I find ${breedName} breeders near ${locationName}?`,
      answerText: `You can find reputable ${breedName} breeders near ${locationName} right here on Paction. Paction features verified breeders throughout ${provinceName} who raise healthy, well-socialized puppies. Use the search results above to view current Listings and Waitlists from breeders in your area. Join a Global Waitlist and Paction will send you a message when we've located an excellent breeder for you.`
    },
    {
      question: `Are there ${breedName} puppies available in ${locationName} right now?`,
      answerText: `${breedName} puppy availability in ${locationName} changes frequently as litters are born and puppies find homes. Check the Listings above to see current availability. If no puppies are available now, you can join a Waitlist to be notified when new litters arrive. Many reputable breeders in ${provinceName} maintain Waitlists for future litter planning.`
    },
    {
      question: `What should I look for in ${withIndefiniteArticle(breedName)} breeder in ${locationName}?`,
      answerText: `When choosing ${withIndefiniteArticle(breedName)} breeder in ${locationName}, look for breeders who perform health testing on parent dogs, provide health guarantees, socialize puppies from an early age, and welcome visits to their breeding program.${healthSentence ? ` ${healthSentence}` : ''} Reputable breeders will be transparent about their breeding practices and happy to answer your questions. To better understand why health testing on the parent dogs is important, check out Paction's Making Science Simple: Canine Genetics for Puppy Shoppers. For more educational information, explore Expert Hub.`
    },
    {
      question: `Can I visit ${breedName} breeders in ${locationName} before buying?`,
      answerText: `Most reputable ${breedName} breeders in ${locationName} and the surrounding ${provinceName} area welcome visits or video calls from potential puppy parents. Meeting the breeder, seeing their facility, and interacting with the parent dogs and puppies is an important part of the process. Contact breeders through Paction to arrange a visit and ask any questions you have about their puppies.`
    }
  );

  return items;
}

/**
 * Generate how-to FAQ items for structured data.
 * Returns only the question and plain text answer (no React nodes).
 */
export function generateHowToStructuredData(
  breedName: string,
  locationName: string
): FAQStructuredDataItem[] {
  return [
    {
      question: `How do I find a responsible ${breedName} breeder in ${locationName}?`,
      answerText: `Finding a responsible ${breedName} breeder in ${locationName} starts with understanding how the breeder plans their litters and not just whether the dogs are registered. Responsible breeders prioritize genetic and health screening of both parents and transparent lineage information. On Paction, breeders commit to intentional and thoughtful pairings that minimize inherited disease risk and they provide clear information about their breeding decisions. To understand more about responsible breeding programs that prioritize health and genetic screening of breeding pairs, check out Making Science Simple: Canine Genetics for Puppy Shoppers.`
    },
    {
      question: `How much does ${withIndefiniteArticle(breedName)} puppy cost in ${locationName}, and why does it vary?`,
      answerText: `The cost of ${withIndefiniteArticle(breedName)} puppy in ${locationName} can vary depending on how the breeder plans and raises their litters. Ethical breeders invest in health and genetic screening, veterinary support, proper socialization, and responsible breeding practices, all of which influence cost. Lower prices may reflect skipped health screening or higher-volume breeding. Paction also emphasizes the ongoing costs of owning a dog is far greater than the initial cost of the puppy itself. To better budget for a new dog, try Paction's Ultimate Dog Cost Calculator.`
    },
    {
      question: `How long is the waitlist for ${withIndefiniteArticle(breedName)} puppy in ${locationName}?`,
      answerText: `Waitlist times for ${withIndefiniteArticle(breedName)} puppy in ${locationName} vary based on breeder demand and how often litters are planned. Responsible breeders typically plan litters when they know they have Canadians waiting for puppies, which can mean longer wait times. This is often a positive sign of intentional breeding and means no puppies will be without good homes. On Paction, puppy buyers can join breed-specific waitlists and receive updates when responsibly planned litters become available, helping families find the right puppy without pressure or rushed decisions.`
    },
    {
      question: `How can I avoid puppy scams when looking for ${withIndefiniteArticle(breedName)} in ${locationName}?`,
      answerText: `Puppy scams are unfortunately common when searching for popular breeds like ${breedName} in ${locationName}. Red flags include breeders unwilling to show health testing, pressure to send deposits quickly, inconsistent information, or puppies offered without clear planning details. Paction reduces scam risk by working with breeders who verify their identities and commit to transparent, documented breeding practices giving puppy buyers a safer starting point when searching locally.`
    }
  ];
}

/**
 * Generate generic puppy-search FAQ items for structured data.
 * These are the static, breed-agnostic questions shown in the PuppiesFAQ component.
 * Included in BreedFAQStructuredData so all FAQPage content lives in one JSON-LD block.
 */
export function generateGenericPuppiesFAQStructuredData(): FAQStructuredDataItem[] {
  return [
    {
      question: 'Where can I find dogs for sale in Canada?',
      answerText:
        'Paction lists dogs for sale from ethical, health-tested breeders across Canada. You can search by breed and province to find your ideal companion.'
    },
    {
      question: 'Can I find dogs for adoption on Paction?',
      answerText:
        'Yes. Paction connects you with dogs for adoption from trusted breeders across Canada, including puppies that are ready for rehoming. Browse current listings or join a waitlist to be notified when a new litter is ready.'
    },
    {
      question: 'Are mixed breed pups available on Paction?',
      answerText:
        'Yes. Alongside purebreds, you can find mixed breed pups from responsible breeders across Canada. Use the breed filter or browse all listings to explore what is currently available.'
    },
    {
      question: 'How do I know a breeder on Paction is reputable?',
      answerText:
        'Every breeder on Paction is vetted before joining the platform. Paction breeders commit to health testing, transparent practices, and lifetime support for the dogs they place. You can read breeder profiles, view available puppies, and message breeders directly before making any decisions.'
    },
    {
      question: 'How much does it cost to buy a puppy in Canada?',
      answerText:
        'Puppy prices vary by breed, breeder, and location. Beyond the purchase price, budgeting for ongoing costs — vet care, food, grooming, and training — is equally important. Paction has built the Ultimate Dog Cost Calculator to help you plan for the full lifetime cost of your dog.'
    }
  ];
}

/**
 * Generate combined FAQ structured data for both LocationFAQ and LocationHowTo.
 */
export function generateCombinedFAQStructuredData(
  breedName: string,
  cityName: string | undefined,
  provinceName: string,
  nicknames?: string[] | null,
  healthScreenings?: string[] | null
): FAQStructuredDataItem[] {
  const isCity = Boolean(cityName);
  const locationName = cityName || provinceName;

  const locationFAQItems = generateLocationFAQStructuredData(
    breedName,
    locationName,
    provinceName,
    isCity,
    nicknames,
    healthScreenings
  );
  const howToItems = generateHowToStructuredData(breedName, locationName);

  return [...locationFAQItems, ...howToItems];
}
